import { PayloadAction, createSlice, isAnyOf } from "@reduxjs/toolkit";

import { UserAxios } from "../../axios/user/user.axios";
import i18n from "../../i18n";
import { AppLangs } from "../../model/shared/enum/AppLangs.enum";
import { User } from "../../model/user/User";
import { ChangePasswordDto } from "../../model/user/dto/ChangePasswordDto";
import { CodeVerifyDto } from "../../model/user/dto/CodeVerifyDto";
import { CreateLoginWithGoogleLinkDto } from "../../model/user/dto/CreateLoginWithGoogleLink";
import { ForgotPasswordEmailDto } from "../../model/user/dto/ForgotPasswordEmailDto";
import { ForgotPasswordNewPasswordDto } from "../../model/user/dto/ForgotPasswordNewPasswordDto";
import { ForgotPasswordTokenDto } from "../../model/user/dto/ForgotPasswordTokenDto";
import { LoginDto } from "../../model/user/dto/LoginDto";
import { LoginResponseDto } from "../../model/user/dto/LoginResponseDto";
import { Login2faResendDto } from "../../model/user/dto/Login2faResendDto";
import { LoginResult } from "../../model/user/dto/LoginResult";
import { Verify2faDto } from "../../model/user/dto/Verify2faDto";
import { Verify2faResponseDto } from "../../model/user/dto/Verify2faResponseDto";
import { LoginWithGoogleDto } from "../../model/user/dto/LoginWithGoogleDto";
import { SignupDto } from "../../model/user/dto/SignupDto";
import { SignupSuccessDto } from "../../model/user/dto/SignupSuccessDto";
import { UpdateUserDto } from "../../model/user/dto/UpdateUserDto";
import { AtiraThunk } from "../AtiraThunk";
import { PSAGetLastUnreadResponseDto } from "../../model/user/dto/PSAGetLastUnreadResponseDto";
import { PSAGetLastUnreadDto } from "../../model/user/dto/PSAGetLastUnreadDto";
import { PSA } from "../../model/admin/AdminPSA";
import { PSAMarkAsReadDto } from "../../model/user/dto/PSAMarkAsReadDto";
import { ResendTokenDto } from "../../model/user/dto/ResendTokenDto";
import { ManageAccountAddDto } from "../../model/user/dto/ManageAccountAddDto";
import { ManageAccountRemoveDto } from "../../model/user/dto/ManageAccountRemoveDto";
import { ManageAccountsSwitchDto } from "../../model/user/dto/ManageAccountsSwitchDto";
import { SetPasswordDto } from "../../model/user/dto/SetPasswordDto";
import { TwoFactorConfigDto } from "../../model/user/dto/TwoFactorConfigDto";
import { TwoFactorConfirmEmailDto } from "../../model/user/dto/TwoFactorConfirmEmailDto";
import { TwoFactorConfirmTotpDto } from "../../model/user/dto/TwoFactorConfirmTotpDto";
import { TotpSetupDto } from "../../model/user/dto/TotpSetupDto";
import { TwoFactorPasswordDto } from "../../model/user/dto/TwoFactorPasswordDto";
import { TwoFactorAxios } from "../../axios/twoFactor/twoFactor.axios";
import {
  IMPERSONATION_KIND_KEY,
  PERSIST_IMPERSONATION_KEY,
  isImpersonationTab,
} from "../store-utils";
import { revertAllReduxAction } from "../store-reset";
import {
  clearTrustedDeviceToken,
  setTrustedDeviceToken,
} from "../../utils/trustedDevice";

interface UserReducer {
  loggedInUserId: string | undefined;
  loggedInUser: User | undefined;
  //
  unreadPSAs: PSA[] | null;
  //
  twoFactor: TwoFactorConfigDto | undefined;
}

const initialState = Object.freeze<UserReducer>({
  loggedInUserId: undefined,
  loggedInUser: undefined,
  unreadPSAs: null,
  twoFactor: undefined,
});

const createLoginWithGoogleLink = AtiraThunk<
  string,
  CreateLoginWithGoogleLinkDto
>(`/auth/google-create-link`, UserAxios.createLoginWithGoogleLink);

const loginWithGoogle = AtiraThunk<SignupSuccessDto, LoginWithGoogleDto>(
  "/auth/google",
  UserAxios.loginWithGoogle,
);

const loginWithGoogleWindow = AtiraThunk<SignupSuccessDto, LoginWithGoogleDto>(
  "/auth/google/window",
  UserAxios.loginWithGoogle,
);

const manageAccountsAdd = AtiraThunk<void, ManageAccountAddDto>(
  "/auth/manage-accounts/add",
  UserAxios.manageAccountsAdd,
);

const manageAccountsRemove = AtiraThunk<void, ManageAccountRemoveDto>(
  "/auth/manage-accounts/remove",
  UserAxios.manageAccountsRemove,
);

const manageAccountsSwitch = AtiraThunk<
  LoginResponseDto,
  ManageAccountsSwitchDto
>("/auth/manage-accounts/switch", UserAxios.manageAccountsSwitch);

const changePassword = AtiraThunk<void, ChangePasswordDto>(
  "/user/change-password",
  UserAxios.updateUserPassword,
);

const setPassword = AtiraThunk<void, SetPasswordDto>(
  "/user/set-password",
  UserAxios.setPassword,
);

const getLoggedInUser = AtiraThunk<User, { userId: string }>(
  "/user/self",
  ({ userId }) => UserAxios.getLoggedInUser(userId),
);

const signup = AtiraThunk<void, SignupDto>(`/user/signup`, (dto) =>
  UserAxios.signup(dto),
);

// After signup
const verifyUserAccount = AtiraThunk<SignupSuccessDto, CodeVerifyDto>(
  `/user/code-verify`,
  (dto) => UserAxios.verifyUserAccount(dto),
);

const login = AtiraThunk<LoginResult, LoginDto>(`/user/login`, (dto) =>
  UserAxios.login(dto),
);

// Step 2 of a 2FA login: exchange the challenge token + code for a real session.
const verifyLogin2fa = AtiraThunk<Verify2faResponseDto, Verify2faDto>(
  `/user/login/verify-2fa`,
  (dto) => UserAxios.verifyLogin2fa(dto),
);

const resendLogin2faCode = AtiraThunk<void, Login2faResendDto>(
  `/user/login/2fa/resend-code`,
  (dto) => UserAxios.resendLogin2faCode(dto),
);

// Settings -> Security & Password -> Two-Factor Authentication.
const getTwoFactorConfig = AtiraThunk<TwoFactorConfigDto, void>(
  "/user/two-factor/config",
  TwoFactorAxios.getConfig,
);

const enableEmail2fa = AtiraThunk<
  { pendingVerification: true; devCode?: string },
  TwoFactorPasswordDto
>("/user/two-factor/email/enable", TwoFactorAxios.enableEmail);

const confirmEmail2fa = AtiraThunk<
  TwoFactorConfigDto,
  TwoFactorConfirmEmailDto
>("/user/two-factor/email/confirm", TwoFactorAxios.confirmEmail);

const disable2fa = AtiraThunk<TwoFactorConfigDto, TwoFactorPasswordDto>(
  "/user/two-factor/disable",
  TwoFactorAxios.disable,
);

const disableEmail2fa = AtiraThunk<TwoFactorConfigDto, TwoFactorPasswordDto>(
  "/user/two-factor/email/disable",
  TwoFactorAxios.disableEmail,
);

const initTotp2fa = AtiraThunk<TotpSetupDto, TwoFactorPasswordDto>(
  "/user/two-factor/totp/init",
  TwoFactorAxios.initTotp,
);

const confirmTotp2fa = AtiraThunk<TwoFactorConfigDto, TwoFactorConfirmTotpDto>(
  "/user/two-factor/totp/confirm",
  TwoFactorAxios.confirmTotp,
);

const disableTotp2fa = AtiraThunk<TwoFactorConfigDto, TwoFactorPasswordDto>(
  "/user/two-factor/totp/disable",
  TwoFactorAxios.disableTotp,
);

const forgotPasswordEmail = AtiraThunk<void | string, ForgotPasswordEmailDto>(
  "/user/reset-password/email",
  (dto) => UserAxios.forgotPasswordEmail(dto),
);

const forgotPasswordToken = AtiraThunk<void, ForgotPasswordTokenDto>(
  "/user/reset-password/token",
  (dto) => UserAxios.forgotPasswordToken(dto),
);

const forgotPasswordNewPassword = AtiraThunk<
  void,
  ForgotPasswordNewPasswordDto
>("/user/reset-password/new", (dto) =>
  UserAxios.forgotPasswordNewPassword(dto),
);

const updateUserInfo = AtiraThunk<void, UpdateUserDto>(
  "/user/:id/update",
  UserAxios.updateUserInfo,
);

const psaGetLastUnread = AtiraThunk<
  PSAGetLastUnreadResponseDto,
  PSAGetLastUnreadDto
>("/user/psa/unread-last", UserAxios.psaGetLastUnread);

const psaMarkAsRead = AtiraThunk<void, PSAMarkAsReadDto>(
  "/user/:id/psa/:psaId/mark-as-read",
  UserAxios.psaMarkAsRead,
);

const resendSignupToken = AtiraThunk<void, ResendTokenDto>(
  `/user/signup/resend-token`,
  UserAxios.resendSignupToken,
);

const resendForgotPasswordToken = AtiraThunk<void, ResendTokenDto>(
  `/user/reset-password/resend-token`,
  UserAxios.resendForgotPasswordToken,
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setLoggedInUserManuallyFromSessionStorage: (
      state,
      action: PayloadAction<User>,
    ) => {
      state.loggedInUser = action.payload;
      state.loggedInUserId = action.payload._id;
    },
    logout: (state) => {
      state.loggedInUser = undefined;
      state.loggedInUserId = undefined;
      state.unreadPSAs = [];
      state.twoFactor = undefined;
    },
  },
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(getLoggedInUser.fulfilled, (state, action) => {
      if (action.payload) {
        state.loggedInUser = action.payload;
        state.loggedInUserId = action.payload._id;
      }
    });

    addCase(psaGetLastUnread.fulfilled, (state, action) => {
      state.unreadPSAs = action.payload.psas;
    });

    addCase(getTwoFactorConfig.fulfilled, (state, action) => {
      state.twoFactor = action.payload;
    });

    addMatcher(
      isAnyOf(
        confirmEmail2fa.fulfilled,
        confirmTotp2fa.fulfilled,
        disable2fa.fulfilled,
        disableEmail2fa.fulfilled,
        disableTotp2fa.fulfilled,
      ),
      (state, action: PayloadAction<TwoFactorConfigDto>) => {
        state.twoFactor = action.payload;

        // Once the last method is gone the server revokes every trusted-device
        // grant; drop this browser's copy too so a stale token isn't left
        // sitting in storage pointing at a device that's no longer trusted.
        if (!action.payload.enabled) {
          clearTrustedDeviceToken();
        }
      },
    );

    addMatcher(isAnyOf(revertAllReduxAction), (state) => {
      state.twoFactor = undefined;
    });

    addMatcher(
      isAnyOf(
        loginWithGoogle.fulfilled,
        verifyUserAccount.fulfilled,
        login.fulfilled,
        verifyLogin2fa.fulfilled,
      ),
      (
        state: UserReducer,
        action: PayloadAction<
          SignupSuccessDto | LoginResult | Verify2faResponseDto
        >,
      ) => {
        // A 2FA-required login resolves with { twoFactorRequired: true } and no
        // token - the session isn't established until verifyLogin2fa. Bail here;
        // Login.tsx moves the user to the code step.
        if (!("token" in action.payload) || !action.payload.token) {
          return;
        }

        localStorage.setItem(
          process.env.REACT_APP_LOCAL_STORAGE_TOKEN_KEY!,
          action.payload.token,
        );

        if ("trustedDeviceToken" in action.payload) {
          const { trustedDeviceToken } = action.payload;
          if (trustedDeviceToken) {
            setTrustedDeviceToken(trustedDeviceToken);
          }
        }

        localStorage.setItem(process.env.REACT_APP_TOUR!, "true");

        state.loggedInUser = action.payload.user;
        state.loggedInUserId = action.payload.user._id;

        // Open the app in the language saved on the account (e.g. the language
        // the visitor picked on the marketing site before signing up).
        // `changeLanguage` persists it and reloads once; the guard prevents a
        // reload loop since localStorage matches after the reload.
        const serverLang = action.payload.user?.settings?.lang?.toLowerCase();
        if (
          serverLang &&
          (Object.values(AppLangs) as string[]).includes(serverLang) &&
          i18n.language?.toLowerCase() !== serverLang
        ) {
          i18n.changeLanguage(serverLang);
        }
      },
    );
  },
});

const logoutAndClearPersistence = () => async (dispatch: any) => {
  // Read this before clearing anything - it is what tells us which session
  // this tab owns.
  const impersonating = isImpersonationTab();

  dispatch(userSlice.actions.logout());

  if (impersonating) {
    // Tear down only this tab's impersonated session. The main account's token
    // lives in localStorage and is shared with every other tab, so clearing it
    // here would log the user out everywhere - including when this runs
    // automatically because an impersonation token expired.
    sessionStorage.removeItem(
      process.env.REACT_APP_LOCAL_STORAGE_TOKEN_IMPERSONATION_KEY!,
    );
    sessionStorage.removeItem(process.env.REACT_APP_IMPERSONATION_FLAG_KEY!);
    sessionStorage.removeItem(IMPERSONATION_KIND_KEY);
    sessionStorage.removeItem(PERSIST_IMPERSONATION_KEY);
  } else {
    localStorage.removeItem(process.env.REACT_APP_LOCAL_STORAGE_TOKEN_KEY!);
  }

  const store = await import("../store").then((m) => m.default);
  await store.persistor.purge();
};

export const userActions = {
  ...userSlice.actions,
  logoutAndClearPersistence,
  loginWithGoogle,
  loginWithGoogleWindow,
  getLoggedInUser,
  createLoginWithGoogleLink,
  signup,
  verifyUserAccount,
  login,
  verifyLogin2fa,
  resendLogin2faCode,
  forgotPasswordEmail,
  forgotPasswordToken,
  forgotPasswordNewPassword,
  updateUserInfo,
  changePassword,
  psaGetLastUnread,
  psaMarkAsRead,
  resendSignupToken,
  resendForgotPasswordToken,
  manageAccountsAdd,
  manageAccountsRemove,
  manageAccountsSwitch,
  setPassword,
  getTwoFactorConfig,
  disableEmail2fa,
  initTotp2fa,
  confirmTotp2fa,
  disableTotp2fa,
  enableEmail2fa,
  confirmEmail2fa,
  disable2fa,
};

export default userSlice.reducer;
